import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import {
  clampNormRectEditor,
  slideGuideLines,
  snapRectToGuides,
  type NormalizedRect,
  type SurfLayer,
} from "@vida/surf-core";

export type DragMode = "move" | "resize-se" | "resize-sw" | "resize-ne" | "resize-nw";

type Options = {
  containerRef: RefObject<HTMLElement | null>;
  rect: NormalizedRect;
  onChange: (rect: NormalizedRect) => void;
  safeZone?: NormalizedRect;
  otherLayers?: SurfLayer[];
  snap?: boolean;
  onGuidesChange?: (guides: ReturnType<typeof slideGuideLines>) => void;
};

export function useNormDrag({
  containerRef,
  rect,
  onChange,
  otherLayers = [],
  snap = true,
  onGuidesChange,
}: Options) {
  const [dragging, setDragging] = useState<DragMode | null>(null);
  const startRef = useRef<{ px: NormalizedRect; pointerX: number; pointerY: number } | null>(null);

  // Latest callbacks/values via ref so the drag effect depends ONLY on `dragging`
  // (props arrive inline from SlideCanvas → new refs every render; depending on them
  // re-ran this effect every render and called setState in the idle branch → render loop).
  const onChangeRef = useRef(onChange);
  const otherLayersRef = useRef(otherLayers);
  const onGuidesChangeRef = useRef(onGuidesChange);
  const snapRef = useRef(snap);
  onChangeRef.current = onChange;
  otherLayersRef.current = otherLayers;
  onGuidesChangeRef.current = onGuidesChange;
  snapRef.current = snap;

  const onPointerDown = useCallback(
    (mode: DragMode) => (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      startRef.current = { px: { ...rect }, pointerX: e.clientX, pointerY: e.clientY };
      setDragging(mode);
    },
    [rect],
  );

  useEffect(() => {
    // Only active while dragging. No setState in the idle branch → no render loop.
    if (!dragging) return;

    const peerRects = otherLayersRef.current.map((l) => ({ x: l.x, y: l.y, w: l.w, h: l.h }));
    const guides = slideGuideLines(peerRects);
    onGuidesChangeRef.current?.(guides);

    const onMove = (e: PointerEvent) => {
      const el = containerRef.current;
      const start = startRef.current;
      if (!el || !start) return;
      const bounds = el.getBoundingClientRect();
      const dx = (e.clientX - start.pointerX) / bounds.width;
      const dy = (e.clientY - start.pointerY) / bounds.height;
      let next = { ...start.px };

      if (dragging === "move") {
        next.x = start.px.x + dx;
        next.y = start.px.y + dy;
      } else {
        if (dragging.includes("e")) next.w = start.px.w + dx;
        if (dragging.includes("w")) {
          next.x = start.px.x + dx;
          next.w = start.px.w - dx;
        }
        if (dragging.includes("s")) next.h = start.px.h + dy;
        if (dragging.includes("n")) {
          next.y = start.px.y + dy;
          next.h = start.px.h - dy;
        }
      }

      next = clampNormRectEditor(next);
      if (snapRef.current) next = snapRectToGuides(next, guides);
      onChangeRef.current(next);
    };

    const onUp = () => {
      setDragging(null);
      startRef.current = null;
      onGuidesChangeRef.current?.([]);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, containerRef]);

  return { onPointerDown, dragging };
}

export function containerSizeFromRef(ref: RefObject<HTMLElement | null>) {
  const el = ref.current;
  if (!el) return { width: 960, height: 540 };
  const r = el.getBoundingClientRect();
  return { width: r.width, height: r.height };
}

export function normRectFromPointer(
  container: HTMLElement,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const bounds = container.getBoundingClientRect();
  const dx = x / bounds.width;
  const dy = y / bounds.height;
  const dw = w / bounds.width;
  const dh = h / bounds.height;
  return clampNormRectEditor({ x: dx, y: dy, w: dw, h: dh });
}
